import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  Output,
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { resolveNotificationRoute } from './notification-route.util';
import { buildTestId, createTestIdScope } from '../shared/testid/testid.util';

@Component({
  selector: 'notification-dialog',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: './notification-dialog.component.html',
  styleUrl: './notification-dialog.component.scss',
})
export class NotificationDialogComponent {
  @Input() active: boolean = false;

  // The vendor's project feed, fed by the topbar host so a notification click
  // can be resolved to the project's live location (see resolveNotificationRoute).
  @Input() projects: any[] = [];

  private _readNotifications: any[] = [];
  private _unreadNotifications: any[] = [];

  // Inputs are accessor-backed so the derived view state is recomputed exactly when an input is
  // (re)assigned — the parent reassigns both arrays on every notification update, and a direct
  // assignment in a unit test triggers the setter too. This replaces the old template-bound getters
  // that recomputed on every change-detection pass.
  @Input()
  set readNotifications(value: any) {
    this._readNotifications = value ?? [];
    this.recomputeDerived();
  }
  get readNotifications(): any {
    return this._readNotifications;
  }

  @Input()
  set unreadNotifications(value: any) {
    this._unreadNotifications = value ?? [];
    this.recomputeDerived();
  }
  get unreadNotifications(): any {
    return this._unreadNotifications;
  }

  // Derived view state — populated by recomputeDerived() when the inputs change, never in a getter.
  allNotifications: { notification: any; isRead: boolean }[] = [];
  allTestIds: string[] = [];
  unreadTestIds: string[] = [];
  readTestIds: string[] = [];

  @Output() modalClosed = new EventEmitter<boolean>();
  @Output() markAllAsRead = new EventEmitter<boolean>();
  @Output() markAsRead = new EventEmitter<any>();
  @Output() markAsUnread = new EventEmitter<any>();

  // Default to "All" so the panel opens on the full history (newest-first) —
  // the vendor sees everything without toggling; unread items stay visually
  // marked and keep their per-item Mark-as-read action.
  activeTab: string = 'all';

  constructor(
    private location: Location,
    private router: Router,
    @Inject(LOCALE_ID) public locale: string
  ) {}

  ngOnInit() {}

  // Recomputes the merged/derived view state. Runs ONLY when an input changes (via the setters),
  // never in a template getter — previously these were getters bound directly in the template, so
  // every change-detection pass re-ran map+sort AND the testId getters re-called allNotifications
  // (~O(N²log N) per CD), which stalled opening the panel for vendors with a lot of history.
  private recomputeDerived(): void {
    const unread = this._unreadNotifications.map((n: any) => ({ notification: n, isRead: false }));
    const read = this._readNotifications.map((n: any) => ({ notification: n, isRead: true }));
    // Newest-first. Each entry wraps the ORIGINAL notification (not a copy), so a Mark-as-read /
    // Mark-as-unread action from the "All" tab emits the same object the Unread/Read tabs would.
    this.allNotifications = [...unread, ...read].sort(
      (a, b) =>
        new Date(b.notification?.createdOn ?? 0).getTime() -
        new Date(a.notification?.createdOn ?? 0).getTime()
    );
    // Each list gets its own fresh dedupe scope (matches the prior per-getter behaviour).
    this.allTestIds = this.buildTestIds(this.allNotifications.map((e) => e.notification));
    this.unreadTestIds = this.buildTestIds(this._unreadNotifications);
    this.readTestIds = this.buildTestIds(this._readNotifications);
  }

  // Per-entry identity for trackBy + testids: the notification's own `id` (a
  // persisted UUID from connect-service) — stable per instance regardless of
  // list order or refresh. Falls back to createdOn/subject only for the
  // theoretical case of an id-less item; without the id, two notifications
  // minted in one server op share a createdOn and would collide (one row lost).
  private notificationIdentity(notification: any): string {
    return String(
      notification?.id ??
        notification?.createdOn ??
        notification?.subject ??
        'notification'
    );
  }

  private buildTestIds(notifications: any[]): string[] {
    const scope = createTestIdScope();
    return notifications.map((n) =>
      buildTestId(scope, 'notification', this.notificationIdentity(n))
    );
  }

  // trackBy so *ngFor reuses DOM rows across re-renders instead of destroying + rebuilding them
  // (each row carries a Material tooltip + icon, which are costly to recreate).
  trackByEntry = (_: number, entry: { notification: any }): string =>
    this.notificationIdentity(entry.notification);
  trackByNotification = (_: number, n: any): string => this.notificationIdentity(n);

  markAllNotificationsRead() {
    this.markAllAsRead.emit();
  }

  markNotificationRead(notification: any) {
    this.markAsRead.emit(notification);
  }

  markNotificationUnread(notification: any) {
    this.markAsUnread.emit(notification);
  }

  notificationClick(notification: any) {
    const resolved = resolveNotificationRoute(notification, this.projects);
    if (!resolved) {
      return;
    }
    this.router.navigate(
      resolved.commands,
      resolved.state ? { state: resolved.state } : {}
    );
    if (resolved.closesDialog) {
      this.closeDialog();
    }
  }

  toggleTab(tab: string) {
    this.activeTab = tab;
    this.location.replaceState(
      this.location.path().split('?')[0],
      `tab=${tab}`
    );
  }

  closeDialog() {
    this.active = false;
    this.modalClosed.emit(this.active);
  }
}
